import { IPaymentGroup } from '@/types/payment-groups';
import { UserRole } from '@/types/user';
import {
  ColumnFiltersState,
  OnChangeFn,
  PaginationState,
} from '@tanstack/react-table';
import { PaymentPageStage, PaymentPageTab } from '../../types';
import {
  ConfirmPaymentsFormValues,
  ConfirmPaymentsModal,
} from '../ConfirmPaymentsModal/ConfirmPaymentsModal';
import { ListPaymentGroupsTable } from '../ListPaymentGroupsTable/ListPaymentGroupsTable';
import { PaymentInTheTable } from '../ListPaymentsTable/ListPayments.types';
import { ListPaymentsTable } from '../ListPaymentsTable/ListPaymentsTable';
import { PaymentStagesNavigator } from '../PaymentStagesNavigator';

type PaymentsListContentProps = {
  // State
  stage: PaymentPageStage;
  columnFilters: ColumnFiltersState;
  paginationState: PaginationState;
  paymentGroupToRetry: IPaymentGroup | null;
  tab: PaymentPageTab;

  // Data
  countsByStage: Record<PaymentPageStage, number>;
  isLoadingCounts: boolean;
  total: number;
  isLoading: boolean;
  shouldRenderPaymentsTable: boolean;
  payments: PaymentInTheTable[];
  paymentGroups: IPaymentGroup[];
  isExporting: boolean;
  mainRole: UserRole | undefined;

  // Handlers
  handleColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
  handlePaginationChange: OnChangeFn<PaginationState>;
  handleStageChange: (stage: PaymentPageStage) => Promise<void>;
  handleExport: () => void;
  handleRetryModalClose: (
    formValues?: ConfirmPaymentsFormValues,
  ) => Promise<void>;
  setPaymentGroupToRetry: (paymentGroup: IPaymentGroup | null) => void;
};

export function PaymentsListContent({
  stage,
  columnFilters,
  paginationState,
  paymentGroupToRetry,
  tab,
  countsByStage,
  isLoadingCounts,
  total,
  isLoading,
  shouldRenderPaymentsTable,
  payments,
  paymentGroups,
  isExporting,
  mainRole,
  handleColumnFiltersChange,
  handlePaginationChange,
  handleStageChange,
  handleExport,
  handleRetryModalClose,
  setPaymentGroupToRetry,
}: PaymentsListContentProps) {
  return (
    <>
      <PaymentStagesNavigator
        value={stage}
        onChange={handleStageChange}
        countsByStage={countsByStage}
        isLoading={isLoadingCounts}
        className="shrink-0"
        mainRole={mainRole}
      />

      {shouldRenderPaymentsTable ? (
        <ListPaymentsTable
          tableOptions={{
            data: payments as PaymentInTheTable[],
            manualPagination: true,
            manualFiltering: true,
            state: {
              isLoading,
              pagination: paginationState,
              columnFilters,
            },
            rowCount: total,
            onPaginationChange: handlePaginationChange,
            onColumnFiltersChange: handleColumnFiltersChange,
          }}
          onExport={handleExport}
          isExporting={isExporting}
          stage={stage}
          hideActions={tab === PaymentPageTab.TO_RECEIVE}
        />
      ) : (
        <>
          <ListPaymentGroupsTable
            tableOptions={{
              data: paymentGroups,
              manualFiltering: true,
              state: {
                isLoading,
                pagination: paginationState,
                columnFilters,
                columnVisibility: {
                  status: false,
                },
              },
              rowCount: total,
              onPaginationChange: handlePaginationChange,
              onColumnFiltersChange: handleColumnFiltersChange,
            }}
            onRetryPayment={setPaymentGroupToRetry}
            onExport={handleExport}
            isExporting={isExporting}
            stage={stage}
            hideActions={tab === PaymentPageTab.TO_RECEIVE}
          />
          {paymentGroupToRetry && (
            <ConfirmPaymentsModal
              key={`${paymentGroupToRetry._id}-retry-payment-modal`}
              paymentIds={paymentGroupToRetry.payments}
              totalToPay={paymentGroupToRetry.amount}
              open={!!paymentGroupToRetry}
              onClose={handleRetryModalClose}
            />
          )}
        </>
      )}
    </>
  );
}
